<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router/composables';
  import { useAuth } from '@/composition/use-auth';
  import { PermissionType } from '@/enums/PermissionType';
  import { useOrdersStore } from '@/stores/orders.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { isIncomingAlertVisible } from '@/utils/incoming-alert.utils';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';

  const ordersStore = useOrdersStore();
  const restaurantStore = useRestaurantStore();
  const auth = useAuth();
  const route = useRoute();
  const router = useRouter();

  const isOpen = ref(false);

  const newOrdersCount = computed(() => ordersStore.newOrders?.length ?? 0);
  const callsCount = computed(() => ordersStore.calls?.length ?? 0);

  const visible = computed(() => {
    if (!restaurantStore.restaurant || !restaurantStore.selectedBranch) {
      return false;
    }

    return isIncomingAlertVisible({
      newOrdersCount: newOrdersCount.value,
      callsCount: callsCount.value,
      effectiveTier: restaurantStore.effectiveTier,
      canManageOrders: auth.hasPermission(PermissionType.OrdersManage),
      isMainPosRoute: route.name === 'restaurantPos',
    });
  });

  // Opening the dropdown acknowledges the current batch on THIS device only:
  // sounds stop, the pill keeps animating until items are actually handled,
  // and any new arrival re-arms the sounds.
  watch(isOpen, (open) => {
    if (!open) return;
    ordersStore.clearUnseenOrders();
    ordersStore.acknowledgeAllCalls();
  });

  function goToPos() {
    isOpen.value = false;
    router.push({ name: 'restaurantPos' }).catch(() => undefined);
  }
</script>

<template>
  <div v-if="visible" class="incoming-alert">
    <v-menu v-model="isOpen" bottom offset-y :close-on-content-click="false">
      <template v-slot:activator="{ on, attrs }">
        <button
          class="alert-pill"
          type="button"
          :aria-label="$t('posV2.incomingAlertLabel')"
          v-bind="attrs"
          v-on="on"
        >
          <span class="pill-ping"></span>
          <span class="pill-content">
            <span v-if="newOrdersCount" class="pill-segment">
              <PosUiIcon name="cart-check" class="segment-icon" />
              {{ newOrdersCount }}
            </span>
            <span v-if="callsCount" class="pill-segment">
              <PosUiIcon name="bell" class="segment-icon" />
              {{ callsCount }}
            </span>
          </span>
        </button>
      </template>
      <div class="alert-panel">
        <div v-if="newOrdersCount" class="panel-row">
          <PosUiIcon name="cart-check" class="row-icon" />
          <span>{{ $tc('posV2.newOrdersCount', newOrdersCount) }}</span>
        </div>
        <div v-if="callsCount" class="panel-row">
          <PosUiIcon name="bell" class="row-icon" />
          <span>{{ $tc('posV2.newCallsCount', callsCount) }}</span>
        </div>
        <PosUiButton class="panel-cta" block @click="goToPos">
          {{ $t('posV2.goToPos') }}
        </PosUiButton>
      </div>
    </v-menu>
  </div>
</template>

<style scoped lang="scss">
  .incoming-alert {
    @apply tw-relative tw-mr-2 tw-flex tw-items-center;

    .alert-pill {
      @apply tw-relative tw-rounded-full;

      .pill-ping {
        @apply tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-top-0 tw-rounded-full tw-bg-red-400 tw-animate-ping;
        z-index: 5;
      }

      .pill-content {
        @apply tw-relative tw-flex tw-items-center tw-rounded-xl tw-bg-red-500 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-on-color;
        z-index: 6;

        .pill-segment {
          @apply tw-flex tw-items-center;

          & + .pill-segment {
            @apply tw-ml-2;
          }
        }

        .segment-icon {
          @apply tw-mr-1 tw-text-base;
        }
      }
    }
  }

  .alert-panel {
    @apply tw-flex tw-min-w-[220px] tw-flex-col tw-bg-surface tw-p-4;

    .panel-row {
      @apply tw-mb-2 tw-flex tw-items-center tw-text-sm tw-font-medium tw-text-neutral-800;

      .row-icon {
        @apply tw-mr-2 tw-text-base tw-text-red-600;
      }
    }

    .panel-cta {
      @apply tw-mt-1;
    }
  }
</style>
